import React from 'react';
import { connect } from 'react-redux';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { isScrollingDisabled } from '../../ducks/ui.duck';

import { LayoutSingleColumn, NamedLink, Page } from '../../components';

import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

import css from './AboutPage.module.css';

const ASPECT_CLASSES = {
  // The barn photo as it was taken, uncropped at every width.
  photo: css.imageSlotPhoto,
  // Guro in front of the wardrobe.
  portrait: css.imageSlotPortrait,
  // The garment ready to be photographed.
  still: css.imageSlotStill,
};

/**
 * A photograph in a frame of fixed proportions, so the page does not jump
 * while the image loads. Sources are AVIF assets under
 * /static/images/about/, at roughly twice their rendered width.
 *
 * @param {Object} props
 * @param {'photo'|'portrait'|'still'} props.aspect
 * @param {string} props.src
 * @param {string} [props.alt] - defaults to '' (decorative)
 * @returns {JSX.Element}
 */
const ImageSlot = ({ aspect, src, alt = '' }) => (
  <div className={classNames(css.imageSlot, ASPECT_CLASSES[aspect])}>
    <img src={src} alt={alt} className={css.imageSlotImg} loading="lazy" />
  </div>
);

// Guro's emphasis lives in the copy itself as <b>…</b>, so the lines she set
// in bold stay bold wherever the text is edited.
const bold = chunks => <strong>{chunks}</strong>;

/**
 * One paragraph per message id, in order.
 *
 * @param {Object} props
 * @param {Array<string>} props.ids - message ids
 * @returns {JSX.Element}
 */
const Paragraphs = ({ ids }) => (
  <>
    {ids.map(id => (
      <p key={id} className={css.body}>
        <FormattedMessage id={id} values={{ b: bold }} />
      </p>
    ))}
  </>
);

const range = (prefix, count) => Array.from({ length: count }, (_, i) => `${prefix}${i + 1}`);

// "Hva gjør Zuprize annerledes?": four short blocks, each a title and a text;
// the first closes on a line of its own, set in bold in Guro's document.
const DIFFERENCES = [
  { key: 'Fashion', hasNote: true },
  { key: 'MoreToGive' },
  { key: 'Tidy' },
  { key: 'Simple' },
];

/**
 * About page (`/about`, "Om oss" in the footer and menus).
 *
 * Guro's founder story (her final text, September 2026), told in the first
 * person: the barn market in 2014, two decades in fashion retail, and why
 * that became Zuprize. The homepage introduces the story; this page tells
 * it. Few, clear images, each with room: the 2014 barn photo full width,
 * her portrait beside the text, one still of a garment ready for sale.
 * The copy lives in `nb.json` and is hers; only the punctuation was set
 * typographically (en dashes), as she asked.
 *
 * @param {Object} props
 * @param {boolean} props.scrollingDisabled
 * @returns {JSX.Element}
 */
const AboutPageComponent = props => {
  const intl = useIntl();
  const { scrollingDisabled } = props;

  return (
    <Page
      title={intl.formatMessage({ id: 'AboutPage.metaTitle' })}
      description={intl.formatMessage({ id: 'AboutPage.metaDescription' })}
      scrollingDisabled={scrollingDisabled}
    >
      <LayoutSingleColumn
        topbar={<TopbarContainer currentPage="AboutPage" />}
        footer={<FooterContainer />}
      >
        <article className={css.root}>
          <div className={css.content}>
            {/* 1. Intro: text and air, no image. */}
            <section className={css.hero}>
              <p className={css.eyebrow}>
                <FormattedMessage id="AboutPage.heroEyebrow" />
              </p>
              <h1 className={css.heroHeading}>
                <FormattedMessage id="AboutPage.heroHeading" />
              </h1>
              <p className={css.lede}>
                <FormattedMessage id="AboutPage.heroLede" />
              </p>
              <p className={css.heroBody}>
                <FormattedMessage id="AboutPage.heroBody" />
              </p>
            </section>

            {/* 2. Det startet på låven: the text, then the photo that proves it. */}
            <section className={css.textSection}>
              <h2 className={css.sectionHeading}>
                <FormattedMessage id="AboutPage.originHeading" />
              </h2>
              <Paragraphs ids={range('AboutPage.originBody', 5)} />
            </section>

            <figure className={css.fullImage}>
              <ImageSlot
                aspect="photo"
                src="/static/images/about/about-lavemarked-2014.avif"
                alt={intl.formatMessage({ id: 'AboutPage.originImageAlt' })}
              />
              <figcaption className={css.caption}>
                <FormattedMessage id="AboutPage.originImageCaption" />{' '}
                {/* Credit as plain text. It used to link to the 2014 article in Jarlsberg
                    Avis, but that page is behind the paper's paywall and, on a phone,
                    hard to get back from; Guro chose to keep the credit and drop the link. */}
                <span className={css.credit}>
                  <FormattedMessage id="AboutPage.originImageCredit" />
                </span>
              </figcaption>
            </figure>

            {/* 3. Fra mote og netthandel til Zuprize: text left, portrait right
                on desktop; on a phone the portrait follows the text, full width. */}
            <section className={css.founder}>
              <div className={css.founderText}>
                <h2 className={css.sectionHeading}>
                  <FormattedMessage id="AboutPage.founderHeading" />
                </h2>
                <Paragraphs ids={range('AboutPage.founderBody', 8)} />
              </div>
              <figure className={css.founderFigure}>
                <ImageSlot
                  aspect="portrait"
                  src="/static/images/about/about-guro-klesskap.avif"
                  alt={intl.formatMessage({ id: 'AboutPage.founderImageAlt' })}
                />
                <figcaption className={css.caption}>
                  <FormattedMessage id="AboutPage.founderImageCaption" />
                </figcaption>
              </figure>
            </section>

            {/* 4. Pull quote: large type, nothing else. */}
            <figure className={css.pullQuote}>
              <blockquote className={css.pullQuoteText}>
                <FormattedMessage id="AboutPage.founderQuote" />
              </blockquote>
              <figcaption className={css.pullQuoteSignature}>
                <FormattedMessage id="AboutPage.founderSignature" />
              </figcaption>
            </figure>

            {/* 5. Det vi tror på */}
            <section className={css.textSection}>
              <h2 className={css.sectionHeading}>
                <FormattedMessage id="AboutPage.beliefHeading" />
              </h2>
              <Paragraphs ids={range('AboutPage.beliefBody', 4)} />
            </section>

            {/* 6. Hva gjør Zuprize annerledes?: four short blocks, no photographs. */}
            <section className={css.differences}>
              <h2 className={css.sectionHeading}>
                <FormattedMessage id="AboutPage.differenceHeading" />
              </h2>
              <div className={css.differenceGrid}>
                {DIFFERENCES.map(({ key, hasNote }) => (
                  <div key={key} className={css.difference}>
                    <h3 className={css.differenceTitle}>
                      <FormattedMessage id={`AboutPage.difference${key}Title`} />
                    </h3>
                    <p className={css.body}>
                      <FormattedMessage id={`AboutPage.difference${key}Body`} />
                    </p>
                    {hasNote ? (
                      <p className={css.body}>
                        <strong>
                          <FormattedMessage id={`AboutPage.difference${key}Note`} />
                        </strong>
                      </p>
                    ) : null}
                  </div>
                ))}
              </div>
            </section>

            {/* 7. Hvem vi er til for: text and one still of a garment ready for sale. */}
            <section className={css.split}>
              <div className={css.splitText}>
                <h2 className={css.sectionHeading}>
                  <FormattedMessage id="AboutPage.audienceHeading" />
                </h2>
                <Paragraphs ids={range('AboutPage.audienceBody', 8)} />
              </div>
              <div className={css.splitImage}>
                <ImageSlot
                  aspect="still"
                  src="/static/images/about/about-plagg-klar-for-salg.avif"
                />
              </div>
            </section>

            {/* 8. Hvorfor flere markedsplasser? */}
            <section className={css.textSection}>
              <h2 className={css.sectionHeading}>
                <FormattedMessage id="AboutPage.whyHeading" />
              </h2>
              <Paragraphs ids={range('AboutPage.whyBody', 6)} />
            </section>

            {/* 9. Vår ambisjon */}
            <section className={css.textSection}>
              <h2 className={css.sectionHeading}>
                <FormattedMessage id="AboutPage.ambitionHeading" />
              </h2>
              <Paragraphs ids={range('AboutPage.ambitionBody', 4)} />
            </section>
          </div>

          {/* 10. Lenge leve garderoben: large type and the two ways on. */}
          <section className={css.closing}>
            <h2 className={css.closingHeading}>
              <FormattedMessage id="AboutPage.closingHeading" />
            </h2>
            <p className={css.closingLine}>
              <FormattedMessage id="AboutPage.closingLine" />
            </p>
            <div className={css.closingActions}>
              <NamedLink className={css.closingPrimary} name="HowToSellPage">
                <FormattedMessage id="AboutPage.closingSell" />
              </NamedLink>
              <NamedLink className={css.closingSecondary} name="LandingPage">
                <FormattedMessage id="AboutPage.closingExplore" />
              </NamedLink>
            </div>
            {/* Quiet links under the story; both are in the footer too. */}
            <p className={css.quietLinks}>
              <NamedLink className={css.quietLink} name="UserTermsPage">
                <FormattedMessage id="AboutPage.userTermsLink" />
              </NamedLink>
              <span aria-hidden="true"> · </span>
              <NamedLink className={css.quietLink} name="HowToBuyPage">
                <FormattedMessage id="AboutPage.howToBuyLink" />
              </NamedLink>
            </p>
          </section>
        </article>
      </LayoutSingleColumn>
    </Page>
  );
};

const mapStateToProps = state => ({
  scrollingDisabled: isScrollingDisabled(state),
});

const AboutPage = connect(mapStateToProps)(AboutPageComponent);

export { AboutPageComponent };
export default AboutPage;
